/** @jsx jsx */
import React, { useState, useEffect, useContext, useCallback } from "react"
import { Link } from "gatsby"
import { useSpring, animated as a, config } from "react-spring"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import { Flex, Button, Text, Link as ExternalLink, jsx } from "theme-ui"

import { Context } from "../../context/MainContext"

import useEventListener from "../../hooks/useEventListener"
import enums from "../../util/enums"
import { isBrowser } from "../../util"
import { underline } from "../../mixins"

import { Logo } from "../svg"
import Menu from "./Menu"

const FixedNav = ({ links = [], defaultHidden = true, cartCount }) => {
  const [bounce, toggleBounce] = useState(false)
  const [hidden, setHidden] = useState(defaultHidden)
  const [isOpen, setOpen] = useState(false)
  const {
    store: { caseBuilderMode },
    setIsCartOpen,
    setCaseBuilderMode,
  } = useContext(Context)

  const spring = useSpring({
    config: config.wobbly,
    top: hidden || caseBuilderMode !== enums.mode.view ? -145 : 0,
  })

  const { x } = useSpring({
    from: { x: 1 },
    to: { x: bounce ? 1.1 : 1 },
    delay: 200,
    onRest: () => toggleBounce(false),
    config: config.gentle,
  })

  const handler = useCallback(() => {
    if (!isBrowser) return
    const offsetTop = document.getElementById("header").clientHeight
    const scrollY = window.scrollY
    if (scrollY + 100 > offsetTop) {
      setHidden(false)
    } else if (scrollY < offsetTop) {
      setOpen(false)
      setHidden(true)
    }
  }, [])

  useEventListener("scroll", handler)

  useEffect(() => {
    toggleBounce(!bounce)
  }, [cartCount]) //eslint-disable-line

  const styles = {
    container: {
      fontSize: ["32px", "32px", "32px", "50px"],
      color: "#333",
      fontFamily: "body",
      // mixBlendMode: "difference",
      zIndex: 3,
      display: "grid",
      "a, button": {
        color: "#333",
      },
      gridTemplateColumns: [
        "repeat(6, 1fr)",
        "repeat(6, 1fr)",
        "repeat(6, 1fr)",
        "repeat(12, 1fr)",
      ],
      gridGap: ["5px", "5px", "5px", 2],
      position: "fixed",
      paddingTop: ["16px", "16px", "16px", "20px"],
      width: [
        "calc(100vw - 16px)",
        "calc(100vw - 16px)",
        "calc(100vw - 16px)",
        "calc(100vw - 40px)",
      ],
      top: defaultHidden ? "-145px" : "0px",
    },
    logo: {
      zIndex: 1,
      width: ["230px", "230px", "230px", "355px"],
      position: "absolute",
      top: ["0px", "0px", "0px", "-10px"],
      left: ["-20px", "-20px", "-20px", "-30px"],
    },
    second: {
      zIndex: 1,
      display: ["none", "none", "none", "flex"],
      gridColumn: "7 / 11",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    third: {
      display: "flex",
      zIndex: 1,
      gridColumn: ["6 / -1", "6 / -1", "6 / -1", "11 / -1"],
      flexDirection: "column",
      alignItems: "flex-end",
    },
  }

  return (
    <a.nav
      aria-hidden={hidden}
      gap={2}
      columns={12}
      style={{ ...spring }}
      sx={styles.container}
    >
      <Link to="/" alt="Home">
        <Logo sx={styles.logo} color={"#333"} />
      </Link>
      <Flex sx={styles.second}>
        <Menu
          isOpen={isOpen}
          setOpen={setOpen}
          style={{ ...underline("#333") }}
        >
          <AnchorLink to="/#our-pints">
            <span onClick={() => setOpen(false)}>Our Pints</span>
          </AnchorLink>
          <AnchorLink to="/#our-pints">
            <span
              onClick={() => {
                setOpen(false)
                setCaseBuilderMode(enums.mode.bundle)
              }}
            >
              Build a Case
            </span>
          </AnchorLink>
          <AnchorLink to="/#meet-marco">
            <span onClick={() => setOpen(false)}>Marco’s Story</span>
          </AnchorLink>
          <Link tabIndex={isOpen ? 0 : -1} to="/find-us" title="Find Us">
            <span>Find Us</span>
          </Link>
          <ExternalLink
            title="Shop Marco Merch"
            href="https://www.shop.marcoicecream.com/"
            target="_blank"
            rel="noopener"
          >
            <span>{"Merch"}</span>
          </ExternalLink>
          {links && links.length > 0
            ? links.map(link => (
                <div key={link.title}>
                  <Link
                    tabIndex={isOpen ? 0 : -1}
                    to={link.page.slug.current}
                    title={link.title}
                  >
                    <span>{link.title}</span>
                  </Link>
                </div>
              ))
            : null}
        </Menu>
      </Flex>
      <Flex sx={styles.third}>
        <Button
          onClick={() => setIsCartOpen(true)}
          sx={{
            padding: "0px",
            textAlign: "right",
            background: "none",
          }}
        >
          <Text as="p">Cart</Text>
          <a.div
            style={{
              transform: x.interpolate(x => `scale(${x})`),
            }}
            sx={styles.third}
          >
            <Text as="p">{`(${cartCount})`}</Text>
          </a.div>
        </Button>
      </Flex>
    </a.nav>
  )
}

export default FixedNav
